import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Instagram, Youtube, ChevronDown } from "lucide-react";
import { SITE_LANGS, Flag } from "@/components/LangFlags";
import AvlianaLogo from "@/components/AvlianaLogo";
import AppStoreBadges from "@/components/AppStoreBadges";
import ThemeToggle from "@/components/ThemeToggle";
import { REGION_PAGES } from "@/data/regionPages";
import { FIRMA } from "@/config/firma";

// ---------------------------------------------------------------------------
// Zweistufiger Footer (idealista-Stil), Zypern-fokussiert.
//  • Tier 1 (hell): Regionen, Ratgeber, Service — der SEO-Block, siehe unten.
//  • Tier 2 (anthrazit): Marke, Unternehmen, Recht, App + Sprach-Pillzeile.
// Alle Labels als i18n-Keys — Links/Routen bleiben sprachneutral.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Der obere Footer ist der SEO-Block: Er steht auf JEDER Seite und ist damit
// die staerkste interne Verlinkung der ganzen Site. Umso mehr zaehlt, wohin er
// zeigt.
//
// Stand vor dem 04.08.2026: 16 der 24 Links liefen ins Leere. Die komplette
// erste Spalte ("Wohnungen zum Kauf", "Villen", "Immobilien am Meer" …) zeigte
// auf /suche?…-Filter — und gefilterte Suchen stehen bewusst auf noindex
// (Seo.jsx). Die dritte Spalte lag fast vollstaendig hinter dem Login und ist
// zusaetzlich in robots.txt gesperrt. Der Footer verschenkte also seine
// Linkkraft an Seiten, die nie ranken duerfen, und versprach Nutzern Dinge, die
// er nicht hielt ("Energieausweis beantragen" fuehrte auf das Kontaktformular,
// "Erfolgsgeschichten" auf die Nachrichtenliste).
//
// Jetzt zeigen die drei Spalten auf das, was wirklich indexierbar ist und
// eigenen Inhalt traegt: Regions-Landingpages, Ratgeber-Artikel, Serviceseiten.
// Das Muster entspricht dem, was etablierte Portale tun (Bazaraki fuehrt seine
// Kategorien als echte Pfad-URLs, nicht als Filterparameter).
//
// Die Objekttyp-Einstiege ("Wohnungen zum Kauf") fehlen hier bewusst: Dafuer
// braeuchte es eigene Kategorieseiten (/kaufen/wohnungen o. ae.). Die waeren
// bei aktuell 2-4 Inseraten je Typ duenn — derselbe Grund, aus dem es keine
// Seiten fuer die 29 Einzelorte gibt. Sobald Bestand da ist, gehoeren sie
// hierher; der amtliche Preisindex traegt die Achse bereits (Wohnungen und
// Haeuser laufen deutlich verschieden). Siehe memory/SEO_AUDIT_2026-08-04.md.
// ---------------------------------------------------------------------------

// Spalte 1 steht direkt im JSX weiter unten: die Regions-Landingpages aus
// data/regionPages.js — dieselbe Quelle, aus der Startseite und Seite selbst
// lesen. Das sind unsere echten Kategorieseiten.

// Spalte 2: die Ratgeber-Artikel. Der staerkste eigene Inhalt der Domain und
// bisher aus dem Footer heraus nur zweimal verlinkt.
const COL_GUIDES = [
  ["footer.fg_buy_process", "/ratgeber/immobilienkauf-zypern-ablauf"],
  ["footer.fo_buy_costs", "/ratgeber/kaufnebenkosten-steuern-zypern"],
  ["footer.fg_title_deeds", "/ratgeber/title-deeds-eigentumssicherheit-zypern"],
  ["footer.fg_foreigners", "/ratgeber/immobilienkauf-auslaender-zypern"],
  ["footer.fo_sell_guide", "/ratgeber/immobilie-verkaufen-zypern"],
  ["footer.fg_renting", "/ratgeber/mieten-vermieten-zypern"],
  ["footer.fg_valuation_finance", "/ratgeber/immobilienbewertung-finanzierung-zypern"],
  ["footer.fg_energy", "/ratgeber/energieausweis-nebenkosten-zypern"],
  ["footer.fg_all", "/ratgeber"],
];

// Spalte 3: Service. Nur Ziele, die halten, was das Label verspricht.
// /mein-bereich steht hier bewusst trotz robots.txt-Sperre: Der Eigentuemer-
// bereich ist ein echter Nutzerweg, und ein einzelner gesperrter Link schadet
// nicht — schaedlich war, dass die MEHRHEIT so aussah. Es ist der einzige
// nicht indexierbare Link im ganzen Footer.
//
// /suche OHNE Parameter ist ausdruecklich indexierbar (gemessen: index,follow;
// erst Filterparameter setzen noindex — siehe Search.jsx). Nicht "aufraeumen".
//
// Der Fragment-Link auf /pakete#fuer-makler ist die einzige gewollte
// Mehrfachnennung im ganzen Footer: Es ist dieselbe Seite, aber zwei echte
// Zielgruppen mit zwei Abschnitten. Die Makler-Abos selbst (/makler-pakete,
// /developer-pakete) sind noindex und in robots.txt gesperrt — sie stehen erst
// nach der Anmeldung offen und gehoeren deshalb nicht in den Footer.
const COL_SERVICE = [
  ["footer.fo_all_listings", "/suche"],
  ["footer.fo_valuation", "/wertermittlung"],
  ["footer.fo_financing", "/finanzierung"],
  ["footer.fo_free_listing", "/pakete"],
  ["footer.fo_agent_info", "/pakete#fuer-makler"],
  ["footer.fo_owner_area", "/mein-bereich"],
];

// ---------------------------------------------------------------------------
// Unterer Footer: Unternehmen und Recht. Hier lag das Problem ANDERS als oben:
// alle 19 Links waren indexierbar — aber fuenf zeigten auf /kontakt, weil es
// die versprochenen Seiten nicht gibt. "Presse", "Jobs", "Haeufige Fragen
// (FAQ)" und "Online-Sicherheit" landeten alle auf demselben Formular. Ein
// Label, das eine Seite verspricht und eine andere liefert, ist fuer Nutzer
// eine Sackgasse und fuer Suchmaschinen vierfach derselbe Anker auf dieselbe
// URL (gewertet wird ohnehin nur der erste).
//
// Sie sind entfernt, nicht ersetzt. Sobald es echte Seiten gibt (Pressebereich,
// Stellen, FAQ, Sicherheitshinweise), gehoeren sie hierher zurueck — die
// i18n-Labels dafuer lassen sich in einem Zug wieder anlegen.
//
// /cookie-richtlinie stand bis heute nirgends im Menue verlinkt, obwohl die
// Seite existiert und in der Sitemap steht. Eine Pflichtseite, die man nur
// ueber die Adresszeile erreicht, erfuellt ihren Zweck nicht.
// ---------------------------------------------------------------------------
const ABOUT = [
  ["footer.about_us", "/ueber-uns"],
  ["footer.news", "/aktuelles"],
  ["footer.contact", "/kontakt"],
];

const LEGAL = [
  ["footer.privacy", "/datenschutz"],
  ["footer.cookies", "/cookie-richtlinie"],
  ["footer.imprint", "/impressum"],
  ["footer.terms", "/agb"],
];

// Ratgeber-Ziele als ECHTES <a> statt <Link>: Die Ratgeber existieren bewusst
// nur unpraefixiert (localized=false, einsprachig Deutsch). <Link> erbt aber
// den Router-basename — auf /de/ zeigte jeder Ratgeber-Link auf
// /de/ratgeber/…, und diese URLs liefert Render als leere 987-Byte-Huelle aus
// (kein Snapshot, keine Rewrite-Regel; nachgemessen im Nachaudit 05.08.2026:
// 6 Praefixe x 9 Links = 54 sitewide Huellen-Links). Das <a> verlaesst den
// basename; der Seitenwechsel ist ein Volllade — inhaltlich korrekt, denn die
// unpraefixierte Seite IST ein anderes Dokument (die kanonische Fassung).
// Die UI-Sprache bleibt erhalten: ohne Praefix entscheidet localStorage.
function FussLink({ to, className, children }) {
  if (to.startsWith("/ratgeber")) {
    return <a href={to} className={className}>{children}</a>;
  }
  return <Link to={to} className={className}>{children}</Link>;
}

function LinkCol({ title, items, t }) {
  return (
    <div>
      <h3 className="font-heading text-2xl text-anthracite mb-5">{title}</h3>
      <ul className="flex flex-col gap-3">
        {items.map(([labelKey, to]) => (
          <li key={labelKey}>
            <FussLink to={to} className="text-sm text-anthracite/70 hover:text-mediterranean transition-colors">{t(labelKey)}</FussLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Handy-Fuß (< 768 px, 01.10.2026). Die App hat keinen Fuß; am Handy-Web war
// der Zwei-Stufen-Fuß mit 43 Links auf 44 px Tipphöhe über 2.000 px lang.
// Jetzt im App-Look: die drei Linkspalten als aufklappbare Gruppen (die Links
// bleiben im DOM, die interne Verlinkung für Suchmaschinen also erhalten —
// siehe Kopfkommentar oben), darunter die Pflichtseiten offen sichtbar
// (Impressum nach § 5 DDG „unmittelbar erreichbar") und das Copyright.
// Sprache und Hell/Dunkel stehen am Handy im Menü-Sheet der Kopfzeile.
// ---------------------------------------------------------------------------
function HandyGruppe({ title, children }) {
  return (
    <details className="group border-b border-anthracite/10 last:border-0">
      <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 text-[16px] font-medium text-anthracite [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown className="w-4 h-4 text-appmuted transition-transform group-open:rotate-180" strokeWidth={2.2} />
      </summary>
      <ul className="pb-3">{children}</ul>
    </details>
  );
}

function HandyFuss({ t }) {
  const zeile = "flex min-h-[40px] items-center text-[15px] text-anthracite/75";
  return (
    <div className="md:hidden px-5 pt-8 pb-4" data-testid="footer-phone">
      <div className="rounded-[16px] border border-anthracite/10 bg-surface px-4">
        <HandyGruppe title={t("footer.col_search_title")}>
          {REGION_PAGES.map((r) => (
            <li key={r.slug}><Link to={`/region/${r.slug}`} className={zeile}>{t("footer.fs_region_prefix", { name: r.name })}</Link></li>
          ))}
        </HandyGruppe>
        <HandyGruppe title={t("footer.col_guides_title")}>
          {COL_GUIDES.map(([k, to]) => (
            <li key={k}><FussLink to={to} className={zeile}>{t(k)}</FussLink></li>
          ))}
        </HandyGruppe>
        <HandyGruppe title={t("footer.col_service_title")}>
          {COL_SERVICE.map(([k, to]) => (
            <li key={k}><FussLink to={to} className={zeile}>{t(k)}</FussLink></li>
          ))}
        </HandyGruppe>
        <HandyGruppe title={t("footer.about_title")}>
          {ABOUT.map(([k, to]) => (
            <li key={k}><Link to={to} className={zeile}>{t(k)}</Link></li>
          ))}
        </HandyGruppe>
      </div>
      <nav aria-label={t("footer.legal")} className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1">
        {LEGAL.map(([k, to]) => (
          <Link key={k} to={to} className="inline-flex min-h-[36px] items-center text-[13px] text-appmuted">{t(k)}</Link>
        ))}
      </nav>
      <p className="mt-2 text-center text-[12px] text-appmuted">© {new Date().getFullYear()} Avliana · {FIRMA.email}</p>
    </div>
  );
}

export default function Footer() {
  const { lang, switchLanguage, t } = useLang();

  return (
    <footer className="md:mt-32">
      <HandyFuss t={t} />
      {/* ---------- Tier 1: Themen-Links ---------- */}
      <div className="hidden md:block border-t-2 border-mediterranean bg-sand-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Spalte 1: Regions-Landingpages statt Filter-Links (siehe oben). */}
          <div>
            <h3 className="font-heading text-2xl text-anthracite mb-5">{t("footer.col_search_title")}</h3>
            <ul className="flex flex-col gap-3">
              {REGION_PAGES.map((r) => (
                <li key={r.slug}>
                  <Link to={`/region/${r.slug}`} className="text-sm text-anthracite/70 hover:text-mediterranean transition-colors">
                    {t("footer.fs_region_prefix", { name: r.name })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <LinkCol title={t("footer.col_guides_title")} items={COL_GUIDES} t={t} />
          <LinkCol title={t("footer.col_service_title")} items={COL_SERVICE} t={t} />
        </div>
      </div>

      {/* ---------- Tier 2: Marke / Recht / Regionen / App ---------- */}
      <div className="hidden md:block bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          {/* Vier statt fuenf Spalten: Die Regionen sind in den oberen
              SEO-Block gewandert (dort tragen sie das Keyword im Linktext).
              Zweimal dieselbe Seite im selben Footer waere eine Dublette. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              {/* Einfarbig auf dunklem Grund — Terrakotta auf Anthrazit reisst
                  den Kontrast. Regelwerk: memory/BRAND_AVLIANA.md */}
              <div className="flex items-center gap-2.5">
                <AvlianaLogo className="h-8 w-auto" mono />
                <div className="font-heading text-3xl">Avliana</div>
              </div>
              {/* Slogan bewusst NICHT in Terrakotta: auf Anthrazit nur 2,7:1
                  (WCAG AA verlangt 4,5:1). Weiss/90 traegt die Markenstimme
                  ueber die Serif-Kursive statt ueber die Farbe. */}
              <p className="mt-3 font-heading text-[15px] italic text-white/90">
                {t("brand.slogan")}
              </p>
              <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-[250px] font-body">
                {t("footer.brand_desc")}
              </p>
              {/* Nur die Kontaktadresse. Bis zum 30.09.2026 standen hier
                  „Limassol, Cyprus" und die erfundene Nummer „+357 25 000 000".
                  Anschrift und Telefon gehören bewusst NICHT hierher: Die
                  Anschrift ist die Privatadresse des Inhabers und steht nur im
                  Impressum, wo § 5 DDG sie verlangt; eine Telefonnummer gibt
                  es nicht (Nics Vorgabe, config/firma.js). */}
              <div className="mt-5 flex flex-col gap-2 text-sm text-white/60">
                <span>{FIRMA.email}</span>
              </div>
            </div>

            {/* Über uns */}
            <div>
              <div className="kicker text-olive-light mb-4">{t("footer.about_title")}</div>
              <ul className="flex flex-col gap-2.5">
                {ABOUT.map(([labelKey, to]) => (
                  <li key={labelKey}><Link to={to} className="text-sm text-white/70 hover:text-white transition-colors">{t(labelKey)}</Link></li>
                ))}
              </ul>
            </div>

            {/* Rechtliches */}
            <div>
              <div className="kicker text-olive-light mb-4">{t("footer.legal")}</div>
              <ul className="flex flex-col gap-2.5">
                {LEGAL.map(([labelKey, to]) => (
                  <li key={labelKey}><Link to={to} className="text-sm text-white/70 hover:text-white transition-colors">{t(labelKey)}</Link></li>
                ))}
              </ul>
            </div>

            {/* App holen */}
            <div className="col-span-2 lg:col-span-1">
              <div className="kicker text-olive-light mb-4">{t("footer.app_title")}</div>
              {/* Bis 03.08.2026 standen hier zwei selbstgebaute Pillen („iOS" /
                  „Android") mit Lucide-Symbolen — die erkennt niemand als
                  Store-Knopf wieder. Jetzt die offiziellen Abzeichen; dass sie
                  (noch) nicht klickbar sind, regelt die Komponente selbst.
                  Davor, bis 29.07.2026, waren es href="#" — auf JEDER Seite. Ein
                  Link, der nichts tut, ist schlimmer als kein Link: Der Nutzer
                  hält den Fehler für seinen eigenen. */}
              <AppStoreBadges />
            </div>
          </div>

          {/* Einstellungszeile: Sprache und Hell/Dunkel.
              Der Hell/Dunkel-Schalter stand bis zum 04.08.2026 in der
              Desktop-Kopfzeile und hat sie zu eng gemacht (96 px Pille neben
              Sprachwähler, Nutzernamen und Primärknopf — der Knopf brach auf
              zwei Zeilen um). Hier unten ist der richtige Ort: Es ist eine
              Einstellung, die man einmal trifft, und daneben steht schon die
              Sprachwahl. `dunkel` dreht die Farben, weil der Fuß auf
              Anthrazit steht und die hellen Töne darin verschwänden. */}
          <div className="border-t border-white/10 mt-9 pt-6 flex items-center gap-x-5 gap-y-3 flex-wrap" data-testid="footer-langs">
            <span className="kicker text-white/60">{t("footer.lang_label")}</span>
            <div className="flex items-center gap-2.5 flex-wrap">
              {SITE_LANGS.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    data-testid={`footer-lang-${l.code}`}
                    onClick={() => switchLanguage(l.code)}
                    className={`inline-flex items-center gap-2 h-11 md:h-9 px-4 rounded-control text-sm transition-colors border ${
                      active ? "bg-mediterranean/15 border-mediterranean text-white" : "border-white/20 text-white/70 hover:border-white/50"
                    }`}
                  >
                    <Flag code={l.code} />
                    <span>{l.label}</span>
                    <span className="font-mono text-[9px] tracking-widest opacity-65">{l.code.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fußleiste: Copyright · Hell/Dunkel · Social.
              Der Schalter stand kurzzeitig in der Sprachzeile darüber. Dort
              füllen die sieben Sprachpillen die Zeile, er brach also in eine
              eigene Zeile um und hing rechts allein unter den App-Abzeichen.
              Hier steht er in einer Zeile, die ohnehin existiert und Platz hat
              — zwischen Copyright und den Social-Symbolen, also dort, wo
              Nebensächliches erwartet wird. */}
          <div className="border-t border-white/10 mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
            <span>© {new Date().getFullYear()} Avliana · {t("footer.rights")}</span>
            <div className="flex items-center gap-5">
              <div className="flex items-center gap-2.5">
                <span className="kicker text-white/60">{t("theme.label")}</span>
                <ThemeToggle testId="footer-theme-toggle" dunkel />
              </div>
              <a href="#" aria-label="Instagram" className="inline-flex h-11 w-11 items-center justify-center -m-2 hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href="#" aria-label="YouTube" className="inline-flex h-11 w-11 items-center justify-center -m-2 hover:text-white transition-colors"><Youtube className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
